import { useCallback, useEffect, useRef } from "react";

const isPageHidden = () => (
  typeof document !== "undefined" && document.visibilityState === "hidden"
);

const isAbortError = (error) => error?.name === "AbortError";

/**
 * Single-flight polling for browser pages.
 *
 * The next poll is scheduled only after the current callback settles. Polling
 * pauses while the page is hidden, aborts the active request on cleanup, and
 * resumes immediately when the page becomes visible again.
 */
export const useSequentialPolling = (
  callback,
  intervalMs,
  { enabled = true, immediate = true, pauseWhenHidden = true } = {},
) => {
  const triggerRef = useRef(() => Promise.resolve());

  useEffect(() => {
    let disposed = false;
    let timerId = null;
    let activeController = null;
    let activePromise = null;
    let runImmediatelyAfterActive = false;
    let forceRunAfterActive = false;

    const clearTimer = () => {
      if (timerId != null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    const canRun = () => !pauseWhenHidden || !isPageHidden();

    const schedule = () => {
      clearTimer();
      if (disposed || !enabled || !canRun()) return;
      timerId = window.setTimeout(() => {
        timerId = null;
        void run();
      }, intervalMs);
    };

    const run = ({ queueAfterActive = false } = {}) => {
      if (disposed || !canRun()) return Promise.resolve();
      if (activePromise) {
        if (!queueAfterActive) return activePromise;
        runImmediatelyAfterActive = true;
        forceRunAfterActive = true;
        const currentPromise = activePromise;
        // The active request may contain several parallel fetches. Its finally block starts the
        // queued replacement; adopt that new promise so a manual refresh waits for fresh data.
        return currentPromise.then(() => activePromise || Promise.resolve());
      }

      clearTimer();
      activeController = new AbortController();
      const controller = activeController;
      activePromise = Promise.resolve()
        .then(() => callback({ signal: controller.signal }))
        .catch((error) => {
          if (!controller.signal.aborted && !isAbortError(error)) {
            console.error("[AdminPolling] 请求失败", error);
          }
        })
        .finally(() => {
          // If Promise.all returned after its first rejected child, cancel any sibling request
          // that is still in flight before this polling slot is released.
          if (!controller.signal.aborted) controller.abort();
          if (activeController === controller) activeController = null;
          activePromise = null;
          const shouldRunImmediately = runImmediatelyAfterActive
            && !disposed
            && canRun()
            && (enabled || forceRunAfterActive);
          runImmediatelyAfterActive = false;
          forceRunAfterActive = false;
          if (shouldRunImmediately) {
            void run();
          } else {
            schedule();
          }
        });
      return activePromise;
    };

    const handleVisibilityChange = () => {
      clearTimer();
      if (!canRun()) {
        activeController?.abort();
        return;
      }
      if (!enabled) return;
      if (activePromise) {
        // A quick hidden -> visible transition can happen before the aborted fetch settles.
        // Queue exactly one immediate replacement instead of waiting a full interval.
        runImmediatelyAfterActive = true;
        return;
      }
      void run();
    };

    triggerRef.current = () => run({ queueAfterActive: true });
    if (pauseWhenHidden && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (immediate && enabled) {
      void run();
    } else {
      schedule();
    }

    return () => {
      disposed = true;
      clearTimer();
      activeController?.abort();
      if (pauseWhenHidden && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      triggerRef.current = () => Promise.resolve();
    };
  }, [callback, enabled, immediate, intervalMs, pauseWhenHidden]);

  return useCallback(() => triggerRef.current(), []);
};

export default useSequentialPolling;
